import * as React from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';

interface SkillsDonutChartProps {
  fullProficiencySkills: string[];
  gainedSomeProficiencySkills: string[];
  noProficiencySkills: string[];
  partialProficiencySkills: string[];
  isPostTest?: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  coordinate?: { x: number; y: number };
  payload?: any[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  coordinate,
  payload,
}) => {
  if (!active || !payload || !payload.length || !coordinate) {
    return null;
  }

  const { name, skills } = payload[0].payload;

  // Only dynamic positioning is provided inline.
  const dynamicStyle: React.CSSProperties = {
    left: coordinate.x,
    top: coordinate.y,
  };

  return (
    <div className="custom-tooltip" style={dynamicStyle}>
      {name}:<br /><br />{skills.length > 0 ? skills.join(', ') : '—'}
    </div>
  );
};

export function SkillsDonutChart({
  fullProficiencySkills=[],
  gainedSomeProficiencySkills=[],
  noProficiencySkills=[],
  partialProficiencySkills=[],
  isPostTest,
}: SkillsDonutChartProps) {
  const chartData = [
    {
      color: '#79C933', // $quill-green-vibrant-50
      name: isPostTest ? 'Gained or Maintained Full Proficiency' : 'Full Proficiency',
      skills: fullProficiencySkills,
      value: fullProficiencySkills.length,
    },
    {
      color: '#B0E185', // $quill-green-vibrant-20
      name: 'Gained Some Proficiency',
      skills: gainedSomeProficiencySkills,
      value: gainedSomeProficiencySkills.length,
    },
    {
      color: '#ECB55A', // $quill-gold-50
      name: 'Partial Proficiency',
      skills: partialProficiencySkills,
      value: partialProficiencySkills.length,
    },
    {
      color: '#FCE3BD', // $quill-gold-10
      name: 'No Proficiency',
      skills: noProficiencySkills,
      value: noProficiencySkills.length,
    },
  ];

  const totalSkills = chartData.reduce((acc, item) => acc + item.value, 0);

  if (totalSkills === 0) {
    return (
      <svg
        aria-label="Skill proficiency donut chart (no data)"
        className="no-skills-donut-chart"
        height="76"
        role="img"
        viewBox="0 0 76 76"
        width="76"
      >
        <circle
          cx="38"
          cy="38"
          fill="none"
          r="34"
          stroke="#e0e0e0" // $quill-grey-5
          strokeWidth="8"
        />
        <text
          dominantBaseline="middle"
          fill="#333"
          textAnchor="middle"
          x="38"
          y="38"
        >
          —
        </text>
      </svg>
    );
  }

  return (
    <PieChart
      aria-label="Skill proficiency donut chart"
      height={76}
      role="img"
      width={76}
    >
      <Pie
        cx="50%"
        cy="50%"
        data={chartData}
        dataKey="value"
        innerRadius={30}
        nameKey="name"
        outerRadius={38}
        paddingAngle={0}
      >
        {chartData.map((entry, index) => (
          <Cell fill={entry.color} key={`cell-${index}`} stroke="none" />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
      <text
        dominantBaseline="middle"
        textAnchor="middle"
        x="38"
        y="38"
      >
        {fullProficiencySkills.length} of {totalSkills}
      </text>
    </PieChart>
  );
}

export default SkillsDonutChart
